import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { XMarkIcon, AcademicCapIcon, CalendarIcon, BuildingLibraryIcon, LinkIcon } from '@heroicons/react/24/outline'
import type { CitationPaper } from '@/api/citations'
import clsx from 'clsx'

interface PaperDetailModalProps {
  paper: CitationPaper | null
  isOpen: boolean
  onClose: () => void
  onSaveToLibrary?: (paper: CitationPaper) => void
  onInsertCitation?: (paper: CitationPaper) => void
  showSaveButton?: boolean
  showCitationButton?: boolean
}

const PaperDetailModal: React.FC<PaperDetailModalProps> = ({
  paper,
  isOpen,
  onClose,
  onSaveToLibrary,
  onInsertCitation,
  showSaveButton = false,
  showCitationButton = false,
}) => {
  if (!paper) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-3xl max-h-[90vh] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col pointer-events-auto"
            >
              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-slate-200 dark:border-slate-700">
                <div className="flex-1 pr-4">
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                    {paper.title}
                  </h2>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
                    {paper.authors.length > 0 && (
                      <div className="flex items-center gap-1">
                        <AcademicCapIcon className="w-4 h-4" />
                        <span>{paper.authors.join(', ')}</span>
                      </div>
                    )}
                    {paper.year && (
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        <span>{paper.year}</span>
                      </div>
                    )}
                    {paper.venue && (
                      <div className="flex items-center gap-1">
                        <BuildingLibraryIcon className="w-4 h-4" />
                        <span>{paper.venue}</span>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6 text-slate-500 dark:text-slate-400" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Abstract */}
                {paper.abstract && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">
                      Abstract
                    </h3>
                    <p className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                      {paper.abstract}
                    </p>
                  </div>
                )}

                {/* Metadata */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {paper.doi && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        DOI
                      </h4>
                      <a
                        href={`https://doi.org/${paper.doi}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1"
                      >
                        <LinkIcon className="w-4 h-4" />
                        {paper.doi}
                      </a>
                    </div>
                  )}
                  {paper.pmid && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        PubMed ID
                      </h4>
                      <a
                        href={`https://pubmed.ncbi.nlm.nih.gov/${paper.pmid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1"
                      >
                        <LinkIcon className="w-4 h-4" />
                        {paper.pmid}
                      </a>
                    </div>
                  )}
                  {paper.arxiv_id && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        arXiv ID
                      </h4>
                      <a
                        href={`https://arxiv.org/abs/${paper.arxiv_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1"
                      >
                        <LinkIcon className="w-4 h-4" />
                        {paper.arxiv_id}
                      </a>
                    </div>
                  )}
                  {paper.url && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        URL
                      </h4>
                      <a
                        href={paper.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1 truncate"
                      >
                        <LinkIcon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">View Paper</span>
                      </a>
                    </div>
                  )}
                  {paper.relevance_score && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        Relevance Score
                      </h4>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-teal-500 to-cyan-500 h-2 rounded-full"
                            style={{ width: `${paper.relevance_score * 100}%` }}
                          />
                        </div>
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {(paper.relevance_score * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-6 border-t border-slate-200 dark:border-slate-700">
                <div className="flex gap-3">
                  {paper.doi && (
                    <a
                      href={`https://doi.org/${paper.doi}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center gap-2"
                    >
                      <LinkIcon className="w-4 h-4" />
                      View Paper
                    </a>
                  )}
                </div>
                <div className="flex gap-3">
                  {showCitationButton && onInsertCitation && (
                    <button
                      onClick={() => {
                        onInsertCitation(paper)
                        onClose()
                      }}
                      className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                    >
                      Insert Citation
                    </button>
                  )}
                  {showSaveButton && onSaveToLibrary && (
                    <button
                      onClick={() => {
                        onSaveToLibrary(paper)
                        onClose()
                      }}
                      className="px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:shadow-lg transition-all"
                    >
                      Save to Library
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

export default PaperDetailModal

